import {
  Button,
  SearchField,
  ToggleButton,
  ToggleButtonGroup,
} from "@heroui/react";
import { useMemo } from "react";

import { GeoFilterPanel } from "@/src/components/GeoFilterPanel";
import { useTranslations } from "@/src/i18n/i18n";
import type { AccountSort } from "@/src/pages/Accounts/accounts.type";
import { isAccountSort, SORTS } from "@/src/pages/Accounts/accounts.type";
import type { AccountFilters as Filters } from "@/src/pages/Accounts/hooks/useAccountFilters";
import { DEFAULT_RADIUS } from "@/src/pages/Accounts/hooks/useAccountFilters";

type AccountFiltersProps = {
  filters: Filters;
  term: string;
  onTermChange: (_term: string) => void;
  onChange: (_patch: Partial<Filters>) => void;
};

const SORT_KEY = {
  createdAt: "sort-created",
  username: "sort-username",
} as const satisfies Record<AccountSort, string>;

/**
 * La barre de filtres : texte, tri, et une recherche géographique repliable.
 *
 * Le panneau géographique est le [`GeoFilterPanel`](src/components/GeoFilterPanel.tsx)
 * partagé avec la liste des événements — les deux routes prennent le même trio
 * `lat`/`lng`/`radius`, et deux panneaux séparés finiraient par ne plus se
 * comporter pareil.
 */
export function AccountFilters({
  filters,
  term,
  onTermChange,
  onChange,
}: AccountFiltersProps) {
  const t = useTranslations("accounts");
  const ta = useTranslations("a11y");

  const sortKeys = useMemo(() => [filters.sort], [filters.sort]);

  return (
    <div className="flex flex-col gap-3">
      <SearchField
        aria-label={ta("search-accounts")}
        fullWidth
        value={term}
        onChange={onTermChange}
      >
        <SearchField.Group>
          <SearchField.SearchIcon />
          <SearchField.Input placeholder={t("search-placeholder")} />
          {/* ⚠️ Le bouton d'effacement de HeroUI s'annonce « Clear » en anglais
              en dur — même piège que le `CloseButton`. */}
          <SearchField.ClearButton aria-label={ta("clear-search")} />
        </SearchField.Group>
      </SearchField>

      <div className="flex flex-wrap items-center gap-3">
        <ToggleButtonGroup
          aria-label={ta("sort-accounts")}
          disallowEmptySelection
          selectedKeys={sortKeys}
          selectionMode="single"
          size="sm"
          onSelectionChange={(keys) => {
            for (const key of keys) {
              if (isAccountSort(key)) onChange({ sort: key });
            }
          }}
        >
          {SORTS.map((sort) => (
            <ToggleButton id={sort} key={sort}>
              {t(SORT_KEY[sort])}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* Le sens du tri est une bascule, pas un troisième onglet : il ne
            change pas *ce* qu'on trie, seulement par quel bout. */}
        <Button
          size="sm"
          variant="ghost"
          onPress={() =>
            onChange({ order: filters.order === "asc" ? "desc" : "asc" })
          }
        >
          {t(filters.order === "asc" ? "order-asc" : "order-desc")}
        </Button>
      </div>

      <GeoFilterPanel
        hasCenter={filters.lat !== null}
        place={filters.near}
        radius={filters.radius}
        onClear={() =>
          onChange({
            lat: null,
            lng: null,
            near: "",
            radius: DEFAULT_RADIUS,
          })
        }
        onRadiusChange={(radius) => onChange({ radius })}
        onSelect={(place) =>
          onChange({
            lat: place.latitude,
            lng: place.longitude,
            near: place.label,
          })
        }
      />
    </div>
  );
}
